import { AlertModal, Box, Button, Modal, showToast } from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import ManageAssetCustomViewForm, {
    AssetCustomViewFormValues,
} from "components/asset-management-system/custom-views/ManageAssetCustomViewForm";
import { enableCustomAssetViewRoutes } from "components/router/Routes";
import { AssetCustomView } from "domain/asset-management/assets";
import { assetViewService } from "services/asset-management/AssetCustomViewService";
import { setViews } from "store/assetViews";

const FORM_ID = "createAssetFolderForm";

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

export const CreateAssetFolderModal = (props: Props) => {
    const { t } = useTranslation();
    const [isSubmitDisabled, setIsSubmitDisabled] = React.useState(true);
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const [isErrorModalOpen, setIsErrorModalOpen] = React.useState(false);
    const [existingNames, setExistingNames] = React.useState<string[]>([]);
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (!props.isOpen) {
            return;
        }
        assetViewService
            .fetchAssetRelatedViews()
            .then((views: AssetCustomView[]) => setExistingNames(views.map((view) => view.name)));
    }, [props.isOpen]);

    const handleSubmit = async (values: AssetCustomViewFormValues) => {
        setIsLoading(true);
        const name = values.name.trim();
        try {
            await assetViewService.createAssetCustomView({
                name,
                uuid: "",
                folder: true,
                columns: [],
                filters: [],
                parentUuid: undefined,
            });
            const views = await assetViewService.fetchAssetRelatedViews();
            enableCustomAssetViewRoutes(views);
            dispatch(setViews(views));
            showToast(t("AssetManagement.createAssetCustomView.successMessageFolder"), "success");
            props.onClose();
        } catch (error) {
            try {
                const errorResponse = JSON.parse((error as Error).message);
                if (errorResponse?.error?.code === "DUPLICATE_NAME") {
                    setErrorMessage(t("AssetManagement.createAssetCustomView.duplicateNameError", { name }));
                } else {
                    setErrorMessage(t("AssetManagement.createAssetCustomView.failureMessageFolder"));
                }
            } catch {
                setErrorMessage(t("AssetManagement.createAssetCustomView.failureMessageFolder"));
            }
            setIsErrorModalOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Modal
                title={t("AssetManagement.createAssetCustomView.createFolder")}
                isOpen={props.isOpen}
                hideModal={props.onClose}
                buttons={
                    <Box gap={"Md"}>
                        <Button
                            type="submit"
                            form={FORM_ID}
                            variant={"Primary"}
                            disabled={isSubmitDisabled || isLoading}
                        >
                            {t("Common.create")}
                        </Button>
                        <Button onClick={props.onClose} variant={"Secondary"}>
                            {t("Common.close")}
                        </Button>
                    </Box>
                }
            >
                <ManageAssetCustomViewForm
                    formId={FORM_ID}
                    onSubmit={handleSubmit}
                    folder={true}
                    existingNames={existingNames}
                    onSubmitDisabledChange={setIsSubmitDisabled}
                />
            </Modal>

            <AlertModal
                title={t("Common.failure")}
                variant={"Error"}
                isOpen={isErrorModalOpen}
                hideModal={() => setIsErrorModalOpen(false)}
                buttons={
                    <Button onClick={() => setIsErrorModalOpen(false)} variant={"Primary"}>
                        {t("Common.ok")}
                    </Button>
                }
                isNested
            >
                {errorMessage}
            </AlertModal>
        </>
    );
};
